import { Flex, Box, Icon } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { KSteps } from '@/ui-library/components/molecules';
import { KAvatarList } from '@/ui-library/components/molecules';
import {
  SCHEDULE_FOR_HEADER,
  SCHEDULE_TYPE_BULK_SELF_SCHEDULE,
} from '../constant';
import { KPopover } from '@/ui-library/components/organisms/KPopover';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { KContactCard } from '@/ui-library/components/organisms/KContactCard';
import { useMemo, memo } from 'react';
import { ApplicationData } from '../types/scheduler';
import { Hackerrank } from '@/assets/icons/ats';

interface ScheduleHeaderProps {
  scheduleType: string;
  candidateId?: number;
  stepItems: any;
  onSelect: any;
  contacts?: any[];
  candidate?: any;
  currentJob?: any;
  applicationData?: ApplicationData;
  interviewPlatform?: 'hackerrank' | null;
}

export const ScheduleHeader = memo(
  ({
    scheduleType,
    stepItems,
    onSelect,
    candidateId,
    contacts,
    candidate,
    currentJob,
    applicationData,
    interviewPlatform = null,
  }: ScheduleHeaderProps) => {
    const stageDetails = useMemo(() => {
      return {
        stages: applicationData?.ats_job_stages || [],
        stageId: applicationData?.job_stage_id || 0,
        daysInStage: applicationData?.days_in_stage || 0,
      };
    }, [applicationData]);

    // pre-requisites
    const headerText =
      interviewPlatform === 'hackerrank'
        ? 'Set up interview'
        : SCHEDULE_FOR_HEADER[scheduleType];
    const contact = {
      name: candidate?.name || '',
      avatar: candidate?.image_url || '',
    };
    const candidates = contact?.name ? [contact] : contacts;

    const getKContactCard = useMemo(() => {
      return (
        <KContactCard
          contactId={candidateId}
          candidate={candidate}
          currentJob={currentJob}
          stageDetails={stageDetails}
        />
      );
    }, [candidateId, candidate, currentJob, stageDetails]);

    return (
      <Box borderBottom="1px solid" p="15px 20px" borderColor="border-color">
        {candidates?.length > 0 && (
          <Flex
            mb="15px"
            alignItems="center"
            gap="10px"
            data-test-id="section-scheduler-header"
          >
            <Flex>
              <KText
                color="secondary-900"
                size="md-semi-bold"
                lineHeight="30px"
                data-test-id="text-scheduler-title"
              >
                {headerText}
              </KText>
            </Flex>
            {interviewPlatform === 'hackerrank' && (
              <Flex alignItems="center" gap="6px">
                <Icon
                  as={Hackerrank}
                  width="14px"
                  height="14px"
                  color="ats-primary"
                />
                <KText size="md-semi-bold" color="secondary-900">
                  Hackerrank interview for
                </KText>
              </Flex>
            )}
            <Flex alignItems={'center'}>
              <KAvatarList
                avatarList={candidates}
                showName={scheduleType !== SCHEDULE_TYPE_BULK_SELF_SCHEDULE}
                gap={'10px'}
                size={'md-medium'}
                avatarProps={{
                  className: 'avatar-xs',
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  border: '1px solid #fff',
                  ml: '-20px',
                }}
                containerProps={{ ml: '15px' }}
                data-test-id="scheduler-title-avatar-candidate"
              ></KAvatarList>
              {scheduleType !== SCHEDULE_TYPE_BULK_SELF_SCHEDULE && (
                <Flex ml="6px" opacity={candidates?.length > 0 ? 1 : 0}>
                  <KPopover
                    // popoverActions={{ isOpen: true }}
                    popoverProps={{ trigger: 'hover' }}
                    triggerElement={
                      <Flex>
                        <Icon
                          cursor="pointer"
                          color="secondary-500"
                          h="18px"
                          w="18px"
                          as={InfoCircle}
                        />
                      </Flex>
                    }
                    children={getKContactCard}
                    showCloseButton={false}
                    popoverContentProps={{ p: '24px' }}
                    popoverBodyProps={{ p: '0px' }}
                  ></KPopover>
                </Flex>
              )}
            </Flex>
          </Flex>
        )}
        <KSteps steps={stepItems as any} onSelect={onSelect} />
      </Box>
    );
  }
);

ScheduleHeader.displayName = 'ScheduleHeader';
